/**
 * Utility functions for Drag and Drop operations within the Track Management system.
 * These functions are pure and designed to be easily testable.
 */

/**
 * Calculates the insertion index for an item being dragged over another item or a container.
 * 
 * @param {Object} params - Parameters for index calculation
 * @param {string|number} params.overId - ID of the element being dragged over
 * @param {Array} params.overItems - Current list of items in the target container
 * @param {Object} params.active - Active drag element from dnd-kit
 * @param {Object} params.over - Over element from dnd-kit
 * @param {number} [params.threshold=0.5] - The vertical threshold (0.0 to 1.0) of the 'over' element 
 *                                          after which the item is considered 'below' it.
 * @returns {number|null} The calculated index, or null if it cannot be determined
 */
export const calculateDragIndex = ({
  overId,
  overItems = [],
  active,
  over,
  threshold = 0.5
}) => {
  if (!overId || !active) return null;

  // Case 1: Dragging directly over a track container (empty space or header)
  if (String(overId).startsWith("track-")) {
    return overItems.length;
  }

  // Case 2: Dragging over a specific item within a track
  const overIndex = overItems.findIndex((item) => item.id === overId);
  if (overIndex === -1) return null;

  const activeRect = active.rect?.current?.translated;
  const overRect = over?.rect;

  if (!activeRect || !overRect) {
    // When rects unavailable, use over index as insert position
    return overIndex;
  }

  // Calculate if we should insert before or after the 'over' item
  // based on whether the active item's top has crossed the threshold of the over item
  const isBelowOverItem = activeRect.top > overRect.top + overRect.height * threshold;
  
  return overIndex + (isBelowOverItem ? 1 : 0);
};

/**
 * Determines if a drag operation has actually changed the position of an item.
 * 
 * @param {Object} params
 * @param {string} params.activeContainer - Source track ID
 * @param {string} params.overContainer - Target track ID
 * @param {number} params.activeIndex - Original index in source track
 * @param {number} params.newIndex - Target index in target track
 * @param {boolean} params.isMultiDrag - Whether multiple items are being moved
 * @returns {boolean} True if the position has changed
 */
export const hasPositionChanged = ({
  activeContainer,
  overContainer,
  activeIndex,
  newIndex,
  isMultiDrag = false
}) => {
  if (activeContainer !== overContainer) return true;
  if (isMultiDrag) return true; // Multi-drag always triggers an update for simplicity
  return activeIndex !== newIndex;
};
